import { Component, TemplateRef, ViewChild } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { TranslatePipe } from '@ngx-translate/core'
import { Column, QueryString } from '../../models'
import { TopicService, TopicV2Service, ViewService } from '../../services'
import { Name, SearchView, Topic } from '../../interfaces'
import { ShortDatePipe, TopicTypePipe } from '../../pipes'
import { CrudSearchResult } from '../../interfaces/crud.service'
import { Observable } from 'rxjs-compat/Observable'
import * as moment from 'moment'
import { Moment } from 'moment'
import { BsModalRef, BsModalService } from 'ngx-bootstrap'
import { RecipientsListModal } from '../../widgets/recipients-list-modal/recipients-list-modal'

@Component({
  selector: 'app-topics',
  templateUrl: './topics.component.html',
  providers: [TranslatePipe]
})

export class TopicsComponent extends SearchView<Topic> {
  public topicTypes: any[]
  public selectedTopic: string | undefined
  public columns: Column []

  public readonly today = moment().startOf('day').valueOf()
  public startDate: Moment | undefined
  public endDate: Moment | undefined

  public selectedAuthors: IdNamePair[] = []
  public selectedStudents: IdNamePair[] = []
  // teachers only ever see their own topics (v2 scopes them server side), so the filter is
  // meaningless for them
  public readonly showAuthorFilter: boolean

  @ViewChild('modalTemplate') modalTemplate!: TemplateRef<any>;
  modalRef?: BsModalRef;

  constructor (
      viewService: ViewService,
      route: ActivatedRoute,
      service: TopicService,
      translatePipe: TranslatePipe,
      private modalService: BsModalService,
      private readonly topicV2Service: TopicV2Service
  ) {
    super(viewService, route, service, 'topics', 'topic')
    this.showAuthorFilter = !viewService.hasUserRole('teacher')
    // default sort
    this.params.sort = { dateTime: -1 }
    this.params.conditions.type = {
      $in: TopicService.TOPIC_COMPONENT_TYPES
    }

    this.topicTypes = this.viewService.optionalList(TopicService.TOPIC_COMPONENT_TYPES, undefined)

    const shortDatePipe = new ShortDatePipe()
    shortDatePipe.locale = viewService.auth.getCurrentUser().locale

    this.columns = [
      this.routerColumn('date', 'TOPIC.DATE', {
        isPrimary: true,
        minWidth: 90,
        maxWidth: 90,
        pipe: shortDatePipe
      }),
      Column.forClick('type', 'TOPIC.TYPE', this.onSelect.bind(this), true, new TopicTypePipe(translatePipe), 90, 90, 'defaultLabelCell'),
      this.routerColumn('title', 'TOPIC.TITLE', {isPrimary: true}),

      new Column('recipients', 'TOPIC.RECIPIENTS', false, undefined, 'topicRecipientsCell', undefined, undefined, undefined, (topic: Topic) => {
        this.openModal(topic)
      }),

      // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
      new Column('audience', 'TOPIC.AUDIENCE', false, null, 'topicAudienceCell', 120, 120),

      // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
      new Column('attachments.length', 'TOPIC.ATTACHMENTS', false, null, 'defaultBadgeCell', 80, 80),

      // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
      new Column('resolvedName', 'TOPIC.AUTHOR', false, null)
    ]
  }

  ngOnInit () {
    // Supervisors/owners/employees have ANY_TOPICS, so v2 returns the whole stage. Start scoped
    // to their own comunicados by preselecting themselves in the author filter; the chip can be
    // removed to see everyone's. Teachers lack ANY_TOPICS: v2 always scopes them server side, so
    // the filter is hidden and there is nothing to seed.
    if (this.showAuthorFilter) {
      const user = this.viewService.getCurrentUser()
      this.selectedAuthors = [new IdNamePair(user._id, this.fullName(user.name) ?? user._id)]
      this.setAuthorsFilter(this.selectedAuthors)
    }
    super.ngOnInit()
  }

  public searchAuthors = (text: string) => this.topicV2Service.autocompleteAuthors(text)
      .map((items) => items.map((item) => new IdNamePair(item._id, item.label)))

  public searchStudents = (text: string) => this.topicV2Service.autocompleteStudents(text)
      .map((items) => items.map((item) => new IdNamePair(item._id, item.label)))

  public setAuthorsFilter (idNamePairs: IdNamePair[]) {
    this.params.conditions.createdBy = idNamePairs.map((e) => e.id)
    this.updateFilter()
  }

  public setStudentsFilter (idNamePairs: IdNamePair[]) {
    this.params.conditions.students = idNamePairs.map((e) => e.id)
    this.updateFilter()
  }

  onStartDateChange (timestamp: number) {
    if (!this.params.conditions.timestamp) {
      this.params.conditions.timestamp = {}
    }
    this.params.conditions.timestamp['$gt'] = timestamp
    this.updateFilter()
  }

  onEndDateChange (timestamp: number) {
    if (!this.params.conditions.timestamp) {
      this.params.conditions.timestamp = {}
    }
    this.params.conditions.timestamp['$lt'] = timestamp
    this.updateFilter()
  }

  
  
  updateTopicFilter () {
    if (this.selectedTopic === undefined) {
      this.params.conditions.type.$in = TopicService.TOPIC_COMPONENT_TYPES
    } else {
      this.params.conditions.type.$in = [this.selectedTopic]
    }
    this.updateFilter()
  }

  // The list is served by the v2 API; the detail page and the recipients modal still use
  // the legacy endpoints through TopicService.
  protected fetch (params: QueryString): Observable<CrudSearchResult<Topic>> {
    return this.topicV2Service.search(params)
  }

  protected onDataFetched (data: CrudSearchResult<Topic>) {
    data.list.forEach((e) => {
      // v2 returns createdBy populated with { _id, name }, so the name comes straight from
      // the row; the legacy id (or an author without a name) is used as a fallback
      const populated = typeof e.createdBy === 'string' ? undefined : e.createdBy
      // @ts-ignore
      e.resolvedName = this.fullName(populated && populated.name) ?? (populated ? populated._id : e.createdBy)
    })
    super.onDataFetched(data)
  }

  openModal(topic: Topic) {
    this.modalRef = this.modalService.show(RecipientsListModal, { initialState: { topic }, class: 'modal-lg' });
  }

  private fullName (name: Name | undefined): string | undefined {
    if (!name) {
      return undefined
    }
    return name.full ?? (name.last + ', ' + name.first)
  }
}

class IdNamePair {
  constructor (readonly id: String, readonly name: String) {
  }
}
